import { action, computed, observable, runInAction } from "mobx"
import { IDisposer } from "mobx-utils"

import { ComponentSpec, DesignerClient, ThemeDto, ThemeSpec, ThemeVersionDto, TokenDto, TokenSpec } from "@/client"
import { createUseStore } from "./util"
import { WebFont } from "@/app/lib/typography/WebFont"
import { id } from "@/client/gen/models/parameters"

export class ThemaStore {
    designer: DesignerClient
    @observable accessor css: string = ''

    @observable accessor theme: Theme | null = null

    @observable accessor themeName: string = "DRAFT"
    @observable accessor versionName: string = ""

    disposer?: IDisposer

    constructor(designer: DesignerClient) {
        this.designer = designer
    }

    async load() {
        const specProm = this.designer.themeSpecGet()
        const draftProm = this.designer.themeDraftGet()

        const [spec, draft] = await Promise.all([specProm, draftProm])

        runInAction(() => {
            console.log("Update from", draft)
            const theme = Theme.FromSpec(spec)
            theme.updateFromDto(draft)
            this.theme = theme
            this.render()
        })
    }

    async restore(id: string, versionId?: string) {
        const theme = await this.designer.themeGet(id)
        const version = versionId ? theme.versions.find(v => v.id == versionId)! : theme.versions[0]!

        await this.designer.themeDraftCreate({theme: version.theme!})
        runInAction(() => {
            console.log("Update from", version)
            this.theme?.updateFromDto(version)
            this.render()
            this.themeName = theme.name || `#${theme.id.slice(0, 4)}`
            this.versionName = version.name || `#${version.id.slice(0, 4)}`
        })
    }

    groups() {
        const groups = this.theme?.groups.map(g => this.group(g.id)!) ?? []
        return groups
    }

    group(id: string): Nullable<Group> {
        const groupSpec = this.theme?.groups.find(g => g.id == id)
        if (!groupSpec) return null
        const components =
            groupSpec?.components
                .map(id => this.theme?.components
                    .find(c => c.id == id))
                .filter(i => !!i)
                ?? []

        return {
            ...groupSpec,
            components
        }
    }

    component(id: string): Nullable<Component> {
        const component = this.theme?.components.find(c => c.id == id)
        if (!component) return null
        else return component
    }

    update() {
        if (!this.theme) return
        this.render()
        this.designer.themeDraftCreate({theme: this.theme})
    }

    @action render() {
        const renderer = new ThemeRenderer
        const css = renderer.render(this.theme!)
        this.css = css
    }

    create = async (name: string) => {
        await this.designer.themeCreate({name})
    }

    save = async ({themeId, name}: {themeId: string, name?: string}) => {
        if (!this.theme) return
        await this.designer.themeVersionCreate(themeId, {name})
    }

    updateTheme = async ({id, versionId, name}: {id: string, versionId?: string, name: string}) => {
        if (versionId)
            await this.designer.themeVersionUpdate(id, versionId, {name})
        else
            await this.designer.themeUpdate(id, {name})
    }

    deleteTheme = async ({id, versionId}: {id: string, versionId?: string}) => {
        if (versionId)
            await this.designer.themeVersionDelete(id, versionId)
        else
            await this.designer.themeDelete(id);
    }

    publish = async ({name, id: groupId, versionId}: {name?: string, id: string, versionId?: string}) => {
        name ??= "My Published Version"
        await this.designer.themePublish(groupId, {name, versionId})
    }
}
export const [ThemaCtx, useThema] = createUseStore(ThemaStore)


export class ThemeRenderer {
    prefix = "hr-"
    defaultSelector = `:root[data-theme="designer"]`
    constructor() {}

    render(theme: Theme): string {
        let output = ""

        const fontFams = theme.components
            .map(c => c.tokens.filter(t => t.type == TokenType.FontFammily))
            .flat()
            .map(t => t.value)
            .filter(t => !!t)

        const fonts: string[] = []

        for (const fam of fontFams) {
            if (!fam) continue
            const webFont = WebFont.FromFamily(fam)
            if (!webFont) continue
            fonts.push(webFont.familyQuery)
        }

        if (fonts.length > 0)
            output += `@import url("https://fonts.googleapis.com/css2?family=${fonts.join("&family=")}&display=swap");\n\n`

        output += "@layer override {\n"
        for (const comp of theme.components) {
            output += `${this.defaultSelector} {\n`
            for (const token of comp.tokens) {
                if (!token.value) continue
                if (token.type == TokenType.Image)
                    output += `--${this.prefix}${token.id}: url("${token.value}");\n`
                else if (token.type == TokenType.FontFammily) {
                    const webFont = WebFont.FromFamily(token.value)
                    if (!webFont) continue
                    output += `--${this.prefix}${token.id}: ${webFont.fontFamily};\n`
                }
                else if (token.type == TokenType.Dimension) {
                    output += `--${this.prefix}${token.id}: ${token.value}${token.unit ?? ""};\n`
                }
                else
                    output += `--${this.prefix}${token.id}: ${token.value};\n`
            }
            output += "}\n"
        }
        output += "}\n"

        return output
    }

}

interface Group {
    id: string
    name: string
    icon?: string
    components: Component[]
}

export enum TokenType {
    Color = "Color",
    Image = "Image",
    String = "String",
    Number = "Number",
    FontFammily = "FontFamily",
    Select = "Select",
    Dimension = "Dimension"
}

export enum Unit {
    Pixels = "px"
}

interface GroupSpec {
    id: string
    name: string
    icon?: string
    components: string[]
}

class Theme {
    @observable accessor id!: string | null
    @observable accessor name!: string | null
    @observable accessor specVersion!: string
    @observable accessor components: Component[] = []
    @observable accessor groups: GroupSpec[] = []

    @computed get tokens() {
        return this.components.map(c => c.tokens).flat()
    }

    static FromSpec(spec: ThemeSpec) {
        const theme = new Theme
        theme.specVersion = spec.version
        theme.components = spec.components.map(c => Component.FromSpec(c))
        theme.groups = spec.groups
        return theme
    }

    @action
    updateFromDto(dto: ThemeVersionDto) {
        const {theme, name, id} = dto
        this.name = name
        this.id = id

        const tokens = this.tokens
        const dtoTokens = theme.components.map(c => c.tokens).flat()
        const tokenMap = new Map(dtoTokens.map(t => [t.id, t]))

        for (const token of tokens) {
            const dtoToken = tokenMap.get(token.id)
            /** Older themes may not have tokens for the latest spec revision. */
            if (!dtoToken) token.value = null
            else token.value = dtoToken.value
        }
    }

    component(id: string): Nullable<Component> {
        const component = this.components.find(c => c.id == id)
        return component ?? null
    }
}

class Component {
    id!: string
    name!: string
    tokens!: Token[]

    static FromSpec(spec: ComponentSpec) {
        const comp = new Component
        comp.id = spec.id
        comp.name = spec.name
        comp.tokens = spec.tokens.map(t => Token.FromSpec(t))
        return comp
    }

    token(id: string): Nullable<Token> {
        const token = this.tokens.find(t => t.id == id)
        return token ?? null
    }
}

interface Option {
    id: string
    name: string
}

class Token {
    type!: TokenType
    id!: string
    name?: string
    description?: string
    options!: Array<Option>
    required?: boolean
    min?: number
    max?: number
    @observable accessor value: string | null = null
    unit?: string

    static FromSpec(spec: TokenSpec) {
        const token = new Token
        token.type = spec.type as TokenType
        token.id = spec.id
        token.name = spec.name
        token.description = spec.description
        token.options = spec.options
        token.min = spec.min
        token.max = spec.max
        token.unit = spec.unit

        return token
    }

    fromDto(dto: TokenDto) {
        this.type = dto.type as TokenType
        this.id = dto.id
        this.value = dto.value ?? null
    }
}